/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import {
  ProviderInterface,
  PatientInterface,
  PatientPharmacyInterface,
  PharmacyInterface,
  PatientInsuranceInterface,
  UserRolesEnum,
} from "@malmur-technologies/medicalsystem-shared";
import PatientForm from "../patient/PatientForm";
import { FormActionType } from "types";
import DoctorForm from "../doctor/DoctorForm";
import { Dispatch, SetStateAction } from "react";

/**
 * Props for the EntityModelForm component.
 * @interface Props
 * @property {any} model - The entity model data (PatientInterface or ProviderInterface)
 * @property {UserRolesEnum} [type] - The user role type to determine which form to render
 * @property {boolean} [isDoctor] - Flag indicating if the provider is a doctor
 * @property {(model: Partial<any>) => void} nextStep - Callback to proceed to next step with updated model
 * @property {() => void} backStep - Callback to return to previous step
 * @property {boolean} lastStep - Flag indicating if this is the last step
 * @property {boolean} firstStep - Flag indicating if this is the first step
 * @property {Dispatch<SetStateAction<PatientPharmacyInterface[]>>} [setPharmacyRelationList] - State setter for pharmacy relations
 * @property {Dispatch<SetStateAction<Partial<PharmacyInterface>>>} [setDefaultPharmacy] - State setter for default pharmacy
 * @property {Dispatch<SetStateAction<PatientInsuranceInterface[]>>} [setInsuranceRelationList] - State setter for insurance relations
 * @property {Dispatch<SetStateAction<File | null>>} [setProviderAssetImage] - State setter for provider asset image
 */
export interface Props {
  model: any;
  type?: UserRolesEnum;
  isDoctor?: boolean;
  nextStep: (model: Partial<any>) => void;
  backStep: () => void;
  lastStep: boolean;
  firstStep: boolean;
  setPharmacyRelationList?: Dispatch<SetStateAction<PatientPharmacyInterface[]>>;
  setDefaultPharmacy?: Dispatch<SetStateAction<Partial<PharmacyInterface>>>;
  setInsuranceRelationList?: Dispatch<SetStateAction<PatientInsuranceInterface[]>>;
  setProviderAssetImage?: Dispatch<SetStateAction<File | null>>;
}

/**
 * Entity form selector component that renders appropriate form based on user role.
 * Routes to PatientForm for patients or DoctorForm for doctors/providers.
 * Used in multi-step form workflows for entity creation.
 * @param {Props} props - Component props
 * @returns {React.ReactElement} The appropriate entity form (Patient or Doctor)
 */
const EntityModelForm = (props: Props) => {
  if (props.type === UserRolesEnum.PATIENT) {
    return (
      <PatientForm
        model={props.model as unknown as PatientInterface}
        action={FormActionType.NEW}
        nextStep={props.nextStep as (model: Partial<PatientInterface>) => void}
        backStepHandler={props.backStep}
        lastStep={props.lastStep}
        firstStep={props.firstStep}
        setPharmacyRelationList={props.setPharmacyRelationList || null}
        setDefaultPharmacy={props.setDefaultPharmacy || null}
        setInsuranceRelationList={props.setInsuranceRelationList || null}
      />
    );
  } else if (props.type === UserRolesEnum.DOCTOR) {
    return (
      <DoctorForm
        model={props.model as unknown as ProviderInterface}
        isDoctor={props.isDoctor}
        action={FormActionType.NEW}
        nextStep={props.nextStep as (model: Partial<ProviderInterface>) => void}
        backStepHandler={props.backStep}
        lastStep={props.lastStep}
        firstStep={props.firstStep}
        setProviderAssetImage={props.setProviderAssetImage}
      />
    );
  }
};

export default EntityModelForm;
